import type { GuardianAlphaNode } from '@/types'
import { Link } from 'react-router-dom'
import { formatKstDateTime } from '@/utils/datetime'
import { isMap, parseAllDocuments } from 'yaml'
import type {
  GuardianAlphaEditorTab,
  GuardianAlphaMetadataDraft,
  GuardianAlphaYamlValidation,
} from './types'
import styles from './guardianAlphaConfig.module.css'


interface Props {
  node: GuardianAlphaNode | null
  activeTab: GuardianAlphaEditorTab
  onSelectTab: (tab: GuardianAlphaEditorTab) => void
  dirtyTabs: Record<GuardianAlphaEditorTab, boolean>
  canDeleteRegistration: boolean
  canWriteSettings: boolean
  canWriteConfig: boolean
  saving: boolean
  metadataDraft: GuardianAlphaMetadataDraft
  yamlText: string
  inputYamlText: string
  yamlValidation: GuardianAlphaYamlValidation
  onMetadataDraftChange: (next: GuardianAlphaMetadataDraft) => void
  onYamlTextChange: (yamlText: string) => void
  onInputYamlTextChange: (yamlText: string) => void
  onSaveMetadata: () => void
  onDeleteNode: (node: GuardianAlphaNode) => void
  onSaveConfig: (apply: boolean) => void
  onReadFile: (kind: 'config' | 'input') => void
  onSaveInput: (apply: boolean) => void
  manualOperatingPath: string | null
  onBeforeNavigate: () => boolean
}

function statusClass(status: string): string {
  if (status === 'succeeded') return styles.statusSucceeded
  if (status === 'failed') return styles.statusFailed
  if (status === 'applying' || status === 'running') return styles.statusBusy
  return styles.statusNeutral
}

function isValidCustomSoundPath(value: string | null): boolean {
  if (!value) return true
  const normalized = value.startsWith('./') ? value.slice(2) : value
  const parts = normalized.split('/')
  return /^[A-Za-z0-9._/-]+$/.test(normalized)
    && parts[0] === 'sounds'
    && parts.length > 1
    && parts.every((part) => part !== '' && part !== '.' && part !== '..')
}

function customActionsAreValid(metadataDraft: GuardianAlphaMetadataDraft): boolean {
  if (metadataDraft.manual_operating_profile !== 'custom') return true
  const actions = metadataDraft.manual_operating_custom_actions
  const keys = new Set(actions.map((action) => action.key))
  return actions.length > 0
    && keys.size === actions.length
    && actions.every((action) => (
      /^[A-Za-z0-9_-]{1,64}$/.test(action.key)
      && action.label.trim().length > 0
      && /^[A-Za-z0-9._-]{1,64}$/.test(action.danger_code)
      && isValidCustomSoundPath(action.sound_path)
    ))
}

export default function GuardianAlphaNodeEditor({
  node,
  activeTab,
  onSelectTab,
  dirtyTabs,
  canDeleteRegistration,
  canWriteSettings,
  canWriteConfig,
  saving,
  metadataDraft,
  yamlText,
  inputYamlText,
  yamlValidation,
  onMetadataDraftChange,
  onYamlTextChange,
  onInputYamlTextChange,
  onSaveMetadata,
  onDeleteNode,
  onSaveConfig,
  onReadFile,
  onSaveInput,
  manualOperatingPath,
  onBeforeNavigate,
}: Props) {
  const inputValidation = validateInputYaml(inputYamlText)
  const customActionsValid = customActionsAreValid(metadataDraft)
  const addCustomAction = () => {
    const key = `custom_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
    onMetadataDraftChange({
      ...metadataDraft,
      manual_operating_custom_actions: [
        ...metadataDraft.manual_operating_custom_actions,
        {
          key,
          label: '',
          danger_code: '',
          sound_path: null,
          tone: 'default',
        },
      ],
    })
  }
  return (
    <section className={`${styles.card} ${styles.editor}`}>
      {!node ? (
        <p className={styles.description}>GuardianNode를 선택하세요.</p>
      ) : (
        <>
          <div className={styles.guardianNodeHeader}>
            <div>
              <div className={styles.guardianNodeTitle}>{node.display_name}</div>
              <div className={styles.guardianNodeKey}>{node.logical_node_key}</div>
            </div>
            <div className={styles.headerActions}>
              <span className={`${styles.statusBadge} ${statusClass(node.last_apply_status)}`}>
                {node.last_apply_status}
              </span>
              {manualOperatingPath && (
                <Link
                  className={styles.linkButton}
                  to={manualOperatingPath}
                  onClick={(event) => {
                    if (!onBeforeNavigate()) event.preventDefault()
                  }}
                >
                  Open Manual Operating
                </Link>
              )}
              {canDeleteRegistration && (
                <button
                  className={styles.dangerButton}
                  onClick={() => onDeleteNode(node)}
                  disabled={saving}
                >
                  Delete registration
                </button>
              )}
            </div>
          </div>

          <div
            className={styles.editorTabs}
            role="tablist"
            aria-label="GuardianNode editor"
          >
            {([
              ['settings', 'Settings'],
              ['config', 'config.yml'],
              ['input', 'input.yml'],
            ] as const).map(([tab, label]) => (
              <button
                key={tab}
                type="button"
                role="tab"
                id={`guardian-alpha-${tab}-tab`}
                aria-controls={`guardian-alpha-${tab}-panel`}
                aria-selected={activeTab === tab}
                tabIndex={activeTab === tab ? 0 : -1}
                className={activeTab === tab ? styles.editorTabActive : styles.editorTab}
                onClick={() => onSelectTab(tab)}
                onKeyDown={(event) => {
                  const tabs: GuardianAlphaEditorTab[] = ['settings', 'config', 'input']
                  const currentIndex = tabs.indexOf(tab)
                  const nextIndex = event.key === 'ArrowRight'
                    ? (currentIndex + 1) % tabs.length
                    : event.key === 'ArrowLeft'
                      ? (currentIndex - 1 + tabs.length) % tabs.length
                      : event.key === 'Home'
                        ? 0
                        : event.key === 'End'
                          ? tabs.length - 1
                          : null
                  if (nextIndex === null) return
                  event.preventDefault()
                  const nextTab = tabs[nextIndex]
                  onSelectTab(nextTab)
                  document.getElementById(`guardian-alpha-${nextTab}-tab`)?.focus()
                }}
              >
                {label}
                {dirtyTabs[tab] && (
                  <span className={styles.unsavedDot} aria-label="unsaved changes">•</span>
                )}
              </button>
            ))}
          </div>

          {activeTab === 'settings' && (
            <div
              role="tabpanel"
              id="guardian-alpha-settings-panel"
              aria-labelledby="guardian-alpha-settings-tab"
              className={styles.tabPanel}
            >
              {canWriteSettings ? (
                <div className={styles.settingsSection}>
              <div>
                <div className={styles.sectionTitle}>GuardianNode settings</div>
                <div className={styles.sectionDescription}>
                  논리 노드 식별 정보와 현장 config 경로를 관리합니다.
                </div>
              </div>

              <div className={styles.metadataGrid}>
                {([
                  ['logical_node_key', 'Logical node key', false],
                  ['display_name', 'Display name', false],
                  ['config_path', 'Config path', true],
                  ['working_directory', 'Working directory', true],
                ] as const).map(([field, label, wide]) => (
                  <label
                    key={field}
                    className={`${styles.label} ${wide ? styles.wideField : ''}`}
                  >
                    <span>{label}</span>
                    <input
                      className={styles.input}
                      value={metadataDraft[field]}
                      onChange={(event) => onMetadataDraftChange({
                        ...metadataDraft,
                        [field]: event.target.value,
                      })}
                    />
                  </label>
                ))}
              </div>

              <details className={styles.advancedSettings}>
                <summary className={styles.advancedSummary}>
                  Advanced process verification
                </summary>
                <p className={styles.advancedDescription}>
                  기본값은 working directory의 app.py 주 프로세스 1개입니다.
                  예외 현장에서만 수정하세요.
                </p>
                <div className={styles.processSettingsGrid}>
                  <label className={styles.label}>
                    <span>Process entrypoint</span>
                    <input
                      className={styles.input}
                      value={metadataDraft.process_entrypoint}
                      onChange={(event) => onMetadataDraftChange({
                        ...metadataDraft,
                        process_entrypoint: event.target.value,
                      })}
                    />
                  </label>
                  <label className={styles.label}>
                    <span>Expected main process count</span>
                    <input
                      type="number"
                      min={1}
                      max={100}
                      className={styles.input}
                      value={metadataDraft.expected_process_count}
                      onChange={(event) => onMetadataDraftChange({
                        ...metadataDraft,
                        expected_process_count: Number(event.target.value),
                      })}
                    />
                  </label>
                </div>
              </details>

              <section className={styles.manualProfileSection}>
                <div>
                  <div className={styles.sectionTitle}>Manual Operating</div>
                  <div className={styles.sectionDescription}>
                    공통 제어 화면에 표시할 시나리오별 명령 구성을 선택합니다.
                  </div>
                </div>
                <label className={styles.label}>
                  <span>Manual Operating profile</span>
                  <select
                    className={styles.input}
                    value={metadataDraft.manual_operating_profile}
                    onChange={(event) => onMetadataDraftChange({
                      ...metadataDraft,
                      manual_operating_profile: event.target.value as
                        GuardianAlphaMetadataDraft['manual_operating_profile'],
                    })}
                  >
                    <option value="">None</option>
                    <option value="ppe">PPE</option>
                    <option value="packer">Packer</option>
                    <option value="custom">Custom</option>
                  </select>
                </label>

                {metadataDraft.manual_operating_profile === 'custom' && (
                  <div className={styles.customActionsEditor}>
                    <div className={styles.customActionsHeader}>
                      <div>
                        <div className={styles.sectionTitle}>Custom actions</div>
                        <div className={styles.sectionDescription}>
                          버튼 label과 위험 코드, 노드의 ./sounds 하위 파일을 설정합니다.
                        </div>
                      </div>
                      <button
                        type="button"
                        className={styles.smallButton}
                        onClick={addCustomAction}
                        disabled={saving || metadataDraft.manual_operating_custom_actions.length >= 20}
                      >
                        Add action
                      </button>
                    </div>
                    {metadataDraft.manual_operating_custom_actions.length === 0 ? (
                      <div className={styles.customActionsEmpty}>
                        Custom profile을 사용하려면 action을 하나 이상 추가하세요.
                      </div>
                    ) : metadataDraft.manual_operating_custom_actions.map((action, index) => (
                      <div className={styles.customActionRow} key={action.key}>
                        <label className={styles.label}>
                          <span>Button label</span>
                          <input
                            className={styles.input}
                            value={action.label}
                            maxLength={60}
                            onChange={(event) => onMetadataDraftChange({
                              ...metadataDraft,
                              manual_operating_custom_actions:
                                metadataDraft.manual_operating_custom_actions.map((item, itemIndex) => (
                                  itemIndex === index ? { ...item, label: event.target.value } : item
                                )),
                            })}
                          />
                        </label>
                        <label className={styles.label}>
                          <span>Danger code</span>
                          <input
                            className={styles.input}
                            value={action.danger_code}
                            maxLength={64}
                            placeholder="SH18001"
                            onChange={(event) => onMetadataDraftChange({
                              ...metadataDraft,
                              manual_operating_custom_actions:
                                metadataDraft.manual_operating_custom_actions.map((item, itemIndex) => (
                                  itemIndex === index
                                    ? { ...item, danger_code: event.target.value }
                                    : item
                                )),
                            })}
                          />
                        </label>
                        <label className={styles.label}>
                          <span>Sound path (optional)</span>
                          <input
                            className={styles.input}
                            value={action.sound_path ?? ''}
                            maxLength={255}
                            placeholder="./sounds/sample.mp3"
                            onChange={(event) => onMetadataDraftChange({
                              ...metadataDraft,
                              manual_operating_custom_actions:
                                metadataDraft.manual_operating_custom_actions.map((item, itemIndex) => (
                                  itemIndex === index
                                    ? { ...item, sound_path: event.target.value || null }
                                    : item
                                )),
                            })}
                          />
                        </label>
                        <label className={styles.label}>
                          <span>Emphasis</span>
                          <select
                            className={styles.input}
                            value={action.tone}
                            onChange={(event) => onMetadataDraftChange({
                              ...metadataDraft,
                              manual_operating_custom_actions:
                                metadataDraft.manual_operating_custom_actions.map((item, itemIndex) => (
                                  itemIndex === index
                                    ? {
                                      ...item,
                                      tone: event.target.value as typeof item.tone,
                                    }
                                    : item
                                )),
                            })}
                          >
                            <option value="default">Default</option>
                            <option value="success">Success</option>
                            <option value="warning">Warning</option>
                            <option value="danger">Danger</option>
                          </select>
                        </label>
                        <button
                          type="button"
                          className={styles.dangerButton}
                          aria-label={`Remove ${action.label || `custom action ${index + 1}`}`}
                          onClick={() => onMetadataDraftChange({
                            ...metadataDraft,
                            manual_operating_custom_actions:
                              metadataDraft.manual_operating_custom_actions.filter(
                                (_item, itemIndex) => itemIndex !== index,
                              ),
                          })}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    {!customActionsValid && (
                      <div className={styles.customActionsError} role="alert">
                        Custom은 label과 영문·숫자 위험 코드가 필요하며, sound path는
                        ./sounds 하위의 정규화된 상대 경로여야 합니다.
                      </div>
                    )}
                  </div>
                )}
              </section>

              <div className={styles.settingsActionRow}>
                <button
                  className={styles.primaryButton}
                  onClick={onSaveMetadata}
                  disabled={saving || !customActionsValid}
                >
                  {saving ? 'Saving...' : 'Save GuardianNode settings'}
                </button>
              </div>
                </div>
              ) : (
                <div className={styles.settingsSection}>
                  <div className={styles.sectionTitle}>GuardianNode settings</div>
                  <div className={styles.metadataSummary}>
                <div className={styles.metadataSummaryItem}>
                  <span className={styles.metadataSummaryLabel}>Config path</span>
                  <code className={styles.metadataSummaryValue}>{node.config_path}</code>
                </div>
                <div className={styles.metadataSummaryItem}>
                  <span className={styles.metadataSummaryLabel}>Working directory</span>
                  <code className={styles.metadataSummaryValue}>{node.working_directory}</code>
                </div>
                <div className={styles.metadataSummaryItem}>
                  <span className={styles.metadataSummaryLabel}>Process entrypoint</span>
                  <code className={styles.metadataSummaryValue}>{node.process_entrypoint}</code>
                </div>
                <div className={styles.metadataSummaryItem}>
                  <span className={styles.metadataSummaryLabel}>Expected main processes</span>
                  <code className={styles.metadataSummaryValue}>
                    {node.expected_process_count}
                  </code>
                </div>
                <div className={styles.metadataSummaryItem}>
                  <span className={styles.metadataSummaryLabel}>Manual Operating profile</span>
                  <code className={styles.metadataSummaryValue}>
                    {node.manual_operating_profile ?? 'None'}
                  </code>
                </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'config' && (
            <div
              role="tabpanel"
              id="guardian-alpha-config-panel"
              aria-labelledby="guardian-alpha-config-tab"
              className={styles.tabPanel}
            >
              <div className={styles.stateBox}>
                <div className={styles.stateItem}>
                  <span className={styles.stateLabel}>Last applied</span>
                  <strong>{formatKstDateTime(node.last_applied_at, '—')}</strong>
                </div>
                <div className={styles.stateItem}>
                  <span className={styles.stateLabel}>Applied hash</span>
                  <code>{node.last_applied_hash?.slice(0, 12) ?? '—'}</code>
                </div>
                {node.last_apply_error && (
                  <span className={styles.statusFailed}>{node.last_apply_error}</span>
                )}
              </div>

              <div className={styles.yamlHeader}>
                <div>
                  <div className={styles.sectionTitle}>config.yml YAML</div>
                  <code className={styles.yamlPath}>{node.config_path}</code>
                  <div className={styles.yamlHashes} aria-label="config.yml hashes">
                    <span>
                      Draft hash{' '}
                      <code title={node.content_sha256 ?? undefined}>
                        {shortHash(node.content_sha256)}
                      </code>
                    </span>
                  </div>
                </div>
                <button
                  className={styles.smallButton}
                  onClick={() => onReadFile('config')}
                  disabled={saving}
                >
                  Read from node
                </button>
              </div>
              <label className={styles.yamlEditorLabel}>
                <textarea
                  value={yamlText}
                  onChange={(event) => onYamlTextChange(event.target.value)}
                  readOnly={!canWriteConfig}
                  spellCheck={false}
                  className={styles.textarea}
                  aria-label="config.yml YAML"
                />
              </label>
              {yamlValidation.error && (
                <div className={styles.error}>{yamlValidation.error}</div>
              )}
              {yamlValidation.hasWeightPath && (
                <div className={styles.weightWarning}>
                  <strong>경고: weight.path는 변경하지 마세요.</strong>
                  <span>
                    모델 배포 경로와 불일치하면 GuardianAlpha 기동이 실패할 수 있습니다.
                  </span>
                </div>
              )}
              {canWriteConfig && (
                <div className={styles.stickyActions}>
                  <button
                    className={styles.smallButton}
                    onClick={() => onSaveConfig(false)}
                    disabled={saving || Boolean(yamlValidation.error)}
                  >
                    Save only
                  </button>
                  <button
                    className={styles.primaryButton}
                    onClick={() => onSaveConfig(true)}
                    disabled={saving || Boolean(yamlValidation.error)}
                  >
                    {saving ? 'Working...' : 'Save & Apply'}
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'input' && (
            <div
              role="tabpanel"
              id="guardian-alpha-input-panel"
              aria-labelledby="guardian-alpha-input-tab"
              className={styles.tabPanel}
            >
              <div className={styles.yamlHeader}>
                <div>
                  <div className={styles.sectionTitle}>input.yml YAML</div>
                  <code className={styles.yamlPath}>{node.input_path}</code>
                  <div className={styles.yamlHashes} aria-label="input.yml hashes">
                    <span>
                      Draft hash{' '}
                      <code title={node.input_content_sha256 ?? undefined}>
                        {shortHash(node.input_content_sha256)}
                      </code>
                    </span>
                    <span>
                      Last Read/source hash{' '}
                      <code title={node.input_source_sha256 ?? undefined}>
                        {shortHash(node.input_source_sha256)}
                      </code>
                    </span>
                  </div>
                </div>
                <button
                  className={styles.smallButton}
                  onClick={() => onReadFile('input')}
                  disabled={saving}
                >
                  Read from node
                </button>
              </div>
              <p className={styles.sectionDescription}>
                input.yml 원문을 주석 포함 그대로 표시합니다. source 및 publish
                자격증명이 평문으로 저장·표시될 수 있으므로 사내 신뢰 환경에서만
                사용하세요.
              </p>
              <label className={styles.yamlEditorLabel}>
                <textarea
                  value={inputYamlText}
                  onChange={(event) => onInputYamlTextChange(event.target.value)}
                  readOnly={!canWriteConfig}
                  spellCheck={false}
                  className={styles.textarea}
                  aria-label="input.yml YAML"
                />
              </label>
              {inputValidation && <div className={styles.error}>{inputValidation}</div>}
              <div className={styles.stateBox}>
                <div className={styles.stateItem}>
                  <span className={styles.stateLabel}>Last Apply state</span>
                  <strong className={statusClass(node.input_last_apply_status)}>
                    {node.input_last_apply_status}
                  </strong>
                </div>
                <div className={styles.stateItem}>
                  <span className={styles.stateLabel}>Last applied</span>
                  <strong>{formatKstDateTime(node.input_last_applied_at, '—')}</strong>
                </div>
                <div className={styles.stateItem}>
                  <span className={styles.stateLabel}>Applied hash</span>
                  <code>{node.input_last_applied_hash?.slice(0, 12) ?? '—'}</code>
                </div>
                {node.input_last_apply_error && (
                  <span className={styles.statusFailed}>{node.input_last_apply_error}</span>
                )}
              </div>
              {canWriteConfig && (
                <div className={styles.stickyActions}>
                  <button
                    className={styles.smallButton}
                    onClick={() => onSaveInput(false)}
                    disabled={saving || Boolean(inputValidation)}
                  >
                    Save only
                  </button>
                  <button
                    className={styles.primaryButton}
                    onClick={() => onSaveInput(true)}
                    disabled={saving || Boolean(inputValidation)}
                  >
                    {saving ? 'Working...' : 'Save & Apply'}
                  </button>
                </div>
              )}
              {canWriteConfig && (
                <div className={styles.description}>
                  Save only는 중앙 draft만 저장합니다. Save &amp; Apply는 필요한 경우 최신
                  input.yml을 자동으로 확인한 뒤 적용합니다.
                </div>
              )}
            </div>
          )}
        </>
      )}
    </section>
  )
}

function shortHash(value: string | null): string {
  return value?.slice(0, 12) ?? '—'
}

export function validateInputYaml(value: string): string | null {
  if (!value) return null
  if (new TextEncoder().encode(value).byteLength > 1024 * 1024) {
    return 'YAML은 1 MiB를 초과할 수 없습니다.'
  }
  const documents = parseAllDocuments(value)
  const parseError = documents.flatMap((document) => document.errors)[0]
  if (parseError) return parseError.message
  if (documents.length !== 1 || !isMap(documents[0].contents)) {
    return '단일 YAML document이며 root가 mapping이어야 합니다.'
  }
  if (value.includes('__KEEP_EXISTING_PASSWORD__')) {
    return '이전 마스킹 형식입니다. Read from node로 원문을 다시 불러오세요.'
  }
  return null
}
